import { Component, inject } from '@angular/core';
import {Subject} from "rxjs";
import {
    BaseTask,
    EmployeeTask,
    EmployeeView,
    ProblemInstanceResult,
    ProblemInstanceService,
    ShiftSplitService
} from "planner-shared";
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {NotificationService, TimeValidators} from "@shared";
import {ShiftSplit} from "../../shared/models/shift-split.model";
import {TimeWindowService} from "../../shared/services/time-window/time-window.service";

@Component({
    selector: 'app-shift-split-modal',
    templateUrl: './shift-split-modal.component.html',
    styleUrls: ['./shift-split-modal.component.scss'],
    standalone: false
})
export class ShiftSplitModalComponent {
    private readonly formBuilder = inject(FormBuilder);
    private readonly shiftSplitService = inject(ShiftSplitService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly notificationService = inject(NotificationService);
    private readonly timeWindowService = inject(TimeWindowService);


    public cancel$: Subject<void>;
    public confirm$: Subject<void>;
    public employee: EmployeeView;

    public shiftSplitForm: FormGroup;

    public splitExists: boolean;
    public isUpdating: boolean;
    public isLoading: boolean;
    public splitTaskName: string;
    public showRequiredTaskWarning: boolean;
    private formEmployee: EmployeeView;

    public set parameter(employee: EmployeeView) {
        this.employee = employee;
        this.formEmployee = {...employee};
        this.initForm();

        this.problemInstanceService.getProblemInstance().subscribe(instanceResult => {
            const splitTask = instanceResult.problemInstance.emTasksNotPatients
                .filter(task => task.isShiftSplitTask && task.employeeId === this.employee.employeeId);
            this.splitExists = splitTask.length > 0;
            if (this.splitExists) {
                this.splitTaskName = splitTask[0].serviceName;
            }
        });
    }

    constructor() {
        this.cancel$ = new Subject();
        this.confirm$ = new Subject();
        this.splitTaskName = "";
        this.employee = {
            fromTime: "",
            toTime: ""
        } as EmployeeView;
        this.formEmployee = this.employee;
        this.initForm();
        this.isUpdating = false;
        this.isLoading = false;
    }

    private initForm(): void {
        this.shiftSplitForm = this.formBuilder.group({
            fromTime: [
                this.formEmployee.fromTime,
                Validators.compose([
                    Validators.required,
                    Validators.minLength(5),
                    Validators.maxLength(5)
                ])
            ],
            toTime: [
                this.formEmployee.toTime,
                Validators.compose([
                    Validators.required,
                    Validators.minLength(5),
                    Validators.maxLength(5)
                ])
            ],
            taskName: [
                this.splitTaskName,
                Validators.maxLength(50)
            ]
        }, {
            validators: [
                TimeValidators.dateAndTimeGreaterThanOrEqual(
                    this.formEmployee.fromDate,
                    'fromTime',
                    this.formEmployee.toDate,
                    'toTime'
                ),
                TimeValidators.timeExceedsWindow(this.employee),
                this.predefinedTasksInWindow(this.formEmployee)
            ]
        });
    }

    public dismissModal(): void {
        this.cancel$.next();
    }

    public confirmModal(): void {
        this.confirm$.next();
    }

    public splitConfirmClicked(): void {
        const shiftSplit: ShiftSplit = {
            employeeId: this.employee.employeeId,
            workHourId: this.employee.workHourId,
            fromTime: this.shiftSplitForm.value.fromTime.replace(':', ""),
            toTime: this.shiftSplitForm.value.toTime.replace(':', ""),
            taskName: this.shiftSplitForm.value.taskName
        };
        let splitTask: EmployeeTask;
        this.problemInstanceService.getProblemInstance().subscribe(instanceResult => {
            if (this.splitExists) {
                this.shiftSplitService.updateShiftSplitTask(this.employee, this.shiftSplitForm, instanceResult.problemInstance);
                this.isUpdating = true;
            } else {
                splitTask = this.shiftSplitService.createShiftSplitTask(this.employee, this.shiftSplitForm, instanceResult.problemInstance);
                instanceResult.problemInstance.emTasksNotPatients.push(splitTask);
            }
            this.isLoading = true;

            this.shiftSplitService.splitShift(shiftSplit).subscribe({
                next: () => {
                    this.isLoading = false;
                    if (this.isUpdating) {
                        this.notificationService.success('SHIFT_SPLIT_UPDATED');
                        this.isUpdating = false;
                    } else {
                        this.notificationService.success('SHIFT_SPLIT_CREATED');
                        this.problemInstanceService.requestProblemInstance().subscribe(requestInstance => {
                            instanceResult.problemInstance.taskWorkShiftCompatibility.push(requestInstance.problemInstance.taskWorkShiftCompatibility.find(task => task.routePlannerTaskId === splitTask.taskId));
                        });
                    }
                    this.confirmModal();
                },
                error: () => {
                    this.isUpdating = false;
                    this.isLoading = false;
                    this.dismissModal();
                }
            });
        });
    }

    public predefinedTasksInWindow = (
        employee: EmployeeView
    ):
        ValidatorFn => {
        return (group: FormGroup): { [key: string]: any } => {
            const fromTimeForm = group.controls['fromTime'].value;
            const toTimeForm = group.controls['toTime'].value;
            let result = {};
            this.showRequiredTaskWarning = false;
            this.problemInstanceService.getProblemInstance().subscribe(
                problemInstanceResult => {
                    const employeeTasks: BaseTask[] =
                        problemInstanceResult.problemInstance.emTasksNotPatients
                            .filter(task => task.employeeId === employee.employeeId && !task.isShiftSplitTask && task.isActive);
                    this.updateRequiredTasksWarningState(problemInstanceResult, fromTimeForm, toTimeForm, employee);
                    employeeTasks.forEach(employeeTask => {
                        if (this.timeWindowService.isTaskOverlappingEmployeeTimeWindow(
                            employeeTask,
                            fromTimeForm,
                            toTimeForm,
                            employee,
                            true
                        )) {
                            result = {
                                predefinedTasksExist: 'predefinedTasksExist'
                            };
                        }
                    });
                }
            );
            return result;
        };
    };

    public getShiftSplitFormError(validationError: ValidationErrors): string {
        let error = "";
        if (validationError.errors) {
            if (validationError.errors.dateAndTimeGreaterThanOrEqual) {
                error = "TASK_END_TIME_VALIDATION";
            } else if (validationError.errors.timeOverlaps) {
                error = "SHIFT_TIME_OVERLAP";
            } else if (validationError.errors.windowExceeds) {
                error = "TIME_WINDOW_EXCEED";
            } else if (validationError.errors.predefinedTasksExist) {
                error = "TIME_WINDOW_OVERLAP";
            }
        }
        return error;
    }

    private updateRequiredTasksWarningState(problemInstanceResult: ProblemInstanceResult, fromTimeForm: string, toTimeForm: string, employee: EmployeeView) {
        problemInstanceResult.problemInstance.taskToReqEmployees.some(task => {
            if (task.employeeIds.includes(this.employee.employeeId)) {
                problemInstanceResult.problemInstance.tasks.some(requiredTask => {
                    if (requiredTask.taskId === task.taskId &&
                        this.timeWindowService.isTaskOverlappingEmployeeTimeWindow(requiredTask, fromTimeForm, toTimeForm, employee, true)) {
                        this.showRequiredTaskWarning = true;
                    }
                });
            }
        });
    }
}
